import { useCallback, useState, useEffect, KeyboardEvent } from "react";
import { debounce } from '@helpers/waitFunctions';
import { useLazyDoSearchAutocompleteQuery } from "@pages/SearchPage/SearchPage.service";

const keyCodes = {
  ENTER: 13,
  ESCAPE: 27,
  UP: 38,
  DOWN: 40,
};

interface useAutocompleteProps {
  query: string;
  value: string;
  setValueToSuggestion: (suggestion: string) => void;
  onEnterKeyPress: () => void;
  onEscKeyPress: () => void;
}

export const useAutocomplete = ({ query, value, onEnterKeyPress, onEscKeyPress, setValueToSuggestion }: useAutocompleteProps) => {
  const [getAutocompleteSuggestions] = useLazyDoSearchAutocompleteQuery();
  const [suggestions, setSuggestions] = useState([]);
  const [selectedSuggestion, setSelectedSuggestion] = useState(-1);

  useEffect(() => {
    // controls the suggestion select process by setting value to currently selected suggestion
    if (selectedSuggestion !== -1) setValueToSuggestion(suggestions[selectedSuggestion]);
  }, [suggestions, selectedSuggestion]);

  const doSelectSuggestion = (key) => {
    const lastItem = suggestions.length - 1;
    let nextItem;

    if (key === keyCodes.UP) {
      nextItem = selectedSuggestion <= 0 ? lastItem : selectedSuggestion - 1;
    } else {
      nextItem = selectedSuggestion === lastItem ? 0 : selectedSuggestion + 1;
    }
    setSelectedSuggestion(nextItem);
  };

  const onKeyDown = (e: KeyboardEvent) => {
    const key = e.which || e.keyCode;
    switch (key) {
      case keyCodes.UP:
      case keyCodes.DOWN:
        e.preventDefault();
        doSelectSuggestion(key);
        break;

      case keyCodes.ENTER:
        onEnterKeyPress();
        break;

      case keyCodes.ESCAPE:
        onEscKeyPress();
        break;
    }
  };


  useEffect(() => {
    if (!value) {
      setSuggestions([]);
      setSelectedSuggestion(-1);
    }
  }, [value]);

  useEffect(() => {
    setSuggestions([]);
    setSelectedSuggestion(-1);
  }, [query]);

  const handleAutocomplete = useCallback(debounce(async (input: string) => {
    if (!input) return;
    const searchTerm = normalizeInput(input);

    const { data: suggestionsData = [] } = await getAutocompleteSuggestions(input);
    const filteredSugestions = suggestionsData.filter((suggestion) =>
      suggestion.match(new RegExp('^' + searchTerm.replace(/\W\s/g, ''), 'i'))
    );

    setSelectedSuggestion(-1);
    setSuggestions(filteredSugestions);
  }, 200), []);


  const normalizeInput = (value) => {
    return value.toLowerCase().trim();
  };

  const resetSuggestions = () => {
    setSuggestions([]);
  }

  return { suggestions, selectedSuggestion, onKeyDown, handleAutocomplete, resetSuggestions };
}